import {
  projectRunFlagsForCreate,
  type ObjectWorkspaceWorkbenchContext,
  type SubmitProjectTurnArgs,
} from '@heidi/agents';
import type {
  LinkedContextItem,
  ProjectRunFlags,
  WorkspaceOpenArtifact,
  WorkspaceSnapshotV1,
} from '@heidi/agents/contracts';
import { composerDraftStorageKey } from '@heidi/agents/data';

import type { ObjectWorkspaceChatTab } from './object-workspace-project-chat-tab';

export interface ObjectWorkspaceChatHarnessState {
  tabs: ObjectWorkspaceChatTab[];
  activeTabId: string;
  announcement?: { id: number; text: string };
}

export type ObjectWorkspaceChatAnnounce = (
  current: ObjectWorkspaceChatHarnessState,
  next: Omit<ObjectWorkspaceChatHarnessState, 'announcement'>,
  text: string
) => ObjectWorkspaceChatHarnessState;

export const normalizedObjectWorkspaceTabTitle = (
  prompt: string,
  fallback: string
): string => prompt.replaceAll('\u200B', '').trim().replace(/\s+/g, ' ') || fallback;

export const objectWorkspaceComposerDraftStorageKey = (
  object: ObjectWorkspaceWorkbenchContext['object'],
  tabId: string
): string =>
  composerDraftStorageKey(`object-workspace:${object.kind}:${object.id}:${tabId}`);

export const emptyObjectWorkspaceChatTab = (
  id: string,
  title: string,
  kind: ObjectWorkspaceChatTab['kind'] = 'work'
): ObjectWorkspaceChatTab => ({
  id,
  title,
  kind,
  isDraftEmpty: true,
  hasQueuedTurns: false,
  isOpeningTurnInFlight: false,
  isTurnInFlight: false,
});

export const isObjectWorkspaceChatTabProtected = (tab: ObjectWorkspaceChatTab): boolean =>
  tab.isOpeningTurnInFlight ||
  tab.isTurnInFlight ||
  tab.hasQueuedTurns ||
  !tab.isDraftEmpty;

export const focusActiveWorkTab = () => {
  window.requestAnimationFrame(() => {
    document
      .querySelector<HTMLElement>(
        '[data-testid="object-workspace-work-tabs"] [role="tab"][data-state="active"]'
      )
      ?.focus();
  });
};

export const linkedContextForObject = ({
  id,
  kind,
  name,
  patientProfileId,
  sourceSessionId,
}: ObjectWorkspaceWorkbenchContext['object']): LinkedContextItem =>
  kind === 'visit'
    ? { type: 'session', sessionId: id, sessionName: name }
    : {
        type: 'patient',
        patientName: name,
        ...(patientProfileId
          ? { patientId: patientProfileId }
          : !sourceSessionId
            ? { patientId: id }
            : {}),
      };

const objectOpenArtifact = (
  object: ObjectWorkspaceWorkbenchContext['object'],
  sessionId: string | undefined
): WorkspaceOpenArtifact | null => {
  if (sessionId) return { type: 'session', session_id: sessionId };
  const linked = linkedContextForObject(object);
  if (linked.type === 'session') return { type: 'session', session_id: linked.sessionId };
  if (linked.type === 'patient' && linked.patientId) {
    return { type: 'patient', patient_id: linked.patientId };
  }
  if (object.sourceSessionId)
    return { type: 'session', session_id: object.sourceSessionId };
  return null;
};

/**
 * The backend grounds each turn in what is on screen, not in the project's
 * create-time link, so an empty canvas reports the workspace's own subject.
 */
export const objectWorkspaceSnapshot = (
  canvas: WorkspaceSnapshotV1,
  object: ObjectWorkspaceWorkbenchContext['object'],
  sessionId: string | undefined
): WorkspaceSnapshotV1 => {
  if (canvas.open_artifact) return canvas;
  const openArtifact = objectOpenArtifact(object, sessionId);
  return openArtifact ? { ...canvas, open_artifact: openArtifact } : canvas;
};

export const objectWorkspaceCreateProjectArgs = (
  args: SubmitProjectTurnArgs,
  title: string,
  {
    linked,
    sessionId,
    runFlags,
  }: {
    linked: LinkedContextItem;
    sessionId: string | undefined;
    runFlags: ProjectRunFlags;
  }
) => ({
  prompt: args.text,
  ...(args.displayText ? { displayPrompt: args.displayText } : {}),
  title,
  ...(args.files?.length ? { files: args.files } : {}),
  ...(args.attachment_ids?.length ? { attachment_ids: args.attachment_ids } : {}),
  ...(args.optimisticAttachments?.length
    ? { optimisticAttachments: args.optimisticAttachments }
    : {}),
  linked,
  sessionIdOverride: sessionId,
  runFlags: projectRunFlagsForCreate(runFlags),
  ...(args.computerUseSkill ? { computerUseSkill: args.computerUseSkill } : {}),
  ...(args.approvalMode ? { approvalMode: args.approvalMode } : {}),
  analytics: {
    work_mode: 'work' as const,
    has_slash_command: args.text.trimStart().startsWith('/'),
  },
});
